import { recognitionMetricKeys, type CaptureProfile, type MetricRecognitionIntervalMs, type RecognitionMetricKey } from '@/domain/types'
import { METRIC_RECOGNITION_INTERVAL_VERSION, normalizeMetricRecognitionIntervalsMs } from './recognition-settings'

export const performanceTiers = ['low', 'medium', 'default', 'high', 'ultra'] as const
export type PerformanceTier = typeof performanceTiers[number]
export interface PerformanceSettings { version: 1; tier: PerformanceTier }
export interface PerformanceConfig {
  readonly captureFpsLimit: number
  readonly processingFpsLimit: number
  readonly metricRecognitionIntervalsMs: Readonly<Record<RecognitionMetricKey, MetricRecognitionIntervalMs>>
}
function config(capture: number, processing: number, fast: MetricRecognitionIntervalMs, slow: MetricRecognitionIntervalMs, map: MetricRecognitionIntervalMs, resource: MetricRecognitionIntervalMs): PerformanceConfig {
  return Object.freeze({ captureFpsLimit: capture, processingFpsLimit: processing,
    metricRecognitionIntervalsMs: Object.freeze({ exp: fast, mesos: fast, hp: resource, mp: resource, level: slow, mapName: map, jobName: 30_000 }) })
}
export const performanceConfigs: Readonly<Record<PerformanceTier, PerformanceConfig>> = Object.freeze({
  low: config(.25, .25, 4_000, 10_000, 4_000, 4_000),
  medium: config(.5, .5, 2_000, 5_000, 4_000, 3_000),
  default: config(1, 1, 1_000, 4_000, 4_000, 2_000),
  high: config(4, 0, 500, 2_000, 2_000, 1_000),
  ultra: config(8, 0, 250, 1_000, 1_000, 500),
})
export function normalizePerformanceSettings(value: unknown): PerformanceSettings {
  const input = value && typeof value === 'object' ? value as Partial<PerformanceSettings> : {}
  return { version: 1, tier: input.version === 1 && performanceTiers.includes(input.tier!) ? input.tier! : 'default' }
}
export function recognitionForTier(recognition: CaptureProfile['recognition'], tier: PerformanceTier): CaptureProfile['recognition'] {
  const { metricCaptureFps: _legacy, ...rest } = recognition
  const normalized = normalizeMetricRecognitionIntervalsMs(recognition.metricRecognitionIntervalsMs)
  const intervals = { ...performanceConfigs[tier].metricRecognitionIntervalsMs }
  for (const metric of recognitionMetricKeys) {
    if (normalized[metric] === 0) intervals[metric] = 0
  }
  return {
    ...rest,
    metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
    metricRecognitionIntervalsMs: normalizeMetricRecognitionIntervalsMs(intervals),
  }
}
export function normalizeCaptureLimit(limit: number): number {
  return [.25, .5, 1, 2, 4, 8, 10].includes(limit) ? limit : 1
}
export function captureFrameRateConstraint(limit: number): ConstrainDouble {
  const fps = normalizeCaptureLimit(limit)
  return { ideal: fps, max: fps }
}
export function limitedProcessingInterval(intervalMs: number, limit: number): number {
  return Number.isFinite(limit) && limit > 0 ? Math.max(intervalMs, 1_000 / limit) : intervalMs
}
export function finalSamplingBudgetMs(captureIntervalMs: number): number {
  return Math.min(10_000, Math.max(2_000, captureIntervalMs * 2 + 2_000))
}
